import { useCallback } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { getRouteApi, useNavigate } from '@mint/routing';

import { CONNECTION_PATHS } from '../connectionPaths.js';
import {
  failConnectionAddOperation,
  succeedConnectionAddOperation,
} from '../operations/connectionAddOperation.js';
import { useAbandonUnfinishedConnectionOperation } from '../operations/connectionOperation.js';
import {
  failConnectionReconnectOperationForOAuthResult,
  succeedConnectionReconnectOperation,
} from '../operations/connectionReconnectOperation.js';
import { markConnectionAuthorized } from '../storage/authorizedConnections.js';
import { AdAccountView } from '../types.js';
import useConnectionOAuthResult from './useConnectionOAuthResult.js';

const updateRouteApi = getRouteApi(
  '/_secured/external-data/integrations/$integration/connections/$connection/update',
);

type OAuthResultSearchParams = {
  oauth_result?: string;
};

export interface UseUpdateConnectionOAuthResultParams {
  /**
   * Re-fetches the connection list after a successful connect, so the newly
   * authorized connection shows up without a manual refresh. This hook has
   * no platform-specific way to fetch a connection list itself (that's what
   * the legacy `useGetConnectionList` did, via a `usePlatformMatcher`
   * if/else this registry exists to remove) — callers pass the registry's
   * own `repository.useList(...).refetch` instead.
   */
  refetchConnectionList: () => Promise<unknown>;
}

/**
 * The generic, platform-agnostic `oauth_result` handling for the
 * manage/update page: translated-error snackbar + navigate back on failure;
 * success snackbar + refetch + switch to the "available accounts" tab +
 * mark the connection authorized on success.
 */
const useUpdateConnectionOAuthResult = ({
  refetchConnectionList,
}: UseUpdateConnectionOAuthResultParams) => {
  const searchParams = updateRouteApi.useSearch() as OAuthResultSearchParams;
  const { integration: platformName, connection } = updateRouteApi.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('@mint-lib/connections-ui');

  const oauthResult = searchParams.oauth_result ?? '';

  useAbandonUnfinishedConnectionOperation();

  // The update page is where both an OAuth add and a reconnect come back;
  // each end call only ends the operation this tab started.
  const handleOAuthError = useCallback(() => {
    failConnectionAddOperation('error');
    failConnectionReconnectOperationForOAuthResult(oauthResult);
    void navigate({
      to: CONNECTION_PATHS.connectionsUpdate,
      params: {
        integration: platformName,
        connection,
      },
      replace: true,
    });
  }, [connection, navigate, oauthResult, platformName]);

  const handleOAuthSuccess = useCallback(() => {
    succeedConnectionAddOperation();
    succeedConnectionReconnectOperation();
    enqueueSnackbar({
      variant: 'success',
      title: t('Account connected:'),
      subTitle: t('Your account has been connected successfully'),
    });

    void refetchConnectionList().then(() => {
      void navigate({
        to: CONNECTION_PATHS.connectionsUpdate,
        params: {
          integration: platformName,
          connection,
        },
        search: { tab: AdAccountView.available },
        replace: true,
      });
    });

    markConnectionAuthorized(connection);
  }, [connection, navigate, platformName, refetchConnectionList, t]);

  useConnectionOAuthResult({
    oauthResult,
    onError: handleOAuthError,
    onSuccess: handleOAuthSuccess,
  });
};

export default useUpdateConnectionOAuthResult;
